import api from '@mint-lib/api';
import { useMutation, useQueryClient } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import { getRouteApi } from '@mint/routing';

import { QueryKey } from '../constants/queryKeys.js';
import {
  CustomColumnSchema,
  type BuilderSchemaResponse,
  type CreateCustomColumnPayload,
  type EditCustomColumnPayload,
} from '../types/builderSchema.js';

const createCustomColumn = async (
  payload: CreateCustomColumnPayload & {
    campaignId: number;
  },
  isMDMFeatureEnabled: boolean,
) => {
  const { campaignId, ...restData } = payload;
  const data = isMDMFeatureEnabled
    ? restData
    : {
        ...restData,
        selectValues: restData.selectValues.map((option) => option.label),
      };
  const response = await api.post('api/datintell/v1/builder/custom_columns/', {
    campaignCollectionId: payload.campaignId,
    customField: {
      ...data,
      dimension: 'custom',
    },
  });

  return CustomColumnSchema.parse(response.data);
};

const editCustomColumn = async (
  payload: EditCustomColumnPayload,
  isMDMFeatureEnabled: boolean,
) => {
  const { id, ...restData } = payload;
  const data = isMDMFeatureEnabled
    ? restData
    : {
        ...restData,
        selectValues: restData.selectValues.map((option) => option.label),
      };

  try {
    await api.put(`api/datintell/v1/builder/custom_columns/${id}/`, data);
    return { id, ...restData };
  } catch {
    return Promise.reject(new Error('Failed to update custom column'));
  }
};

const deleteCustomColumn = async (id: string) => {
  try {
    await api.delete(`api/datintell/v1/builder/custom_columns/${id}/`);
    return { id };
  } catch {
    return Promise.reject(new Error('Failed to delete custom column'));
  }
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

export const useCreateCustomColumn = () => {
  const params = routeApi.useParams();
  const queryClient = useQueryClient();
  const ff = useFlags('@mint/arm', ['mdmColumnsForBuyingPlan']);

  const mutation = useMutation({
    mutationFn: (payload: CreateCustomColumnPayload) =>
      createCustomColumn(
        { ...payload, campaignId: Number(params.campaignId) },
        ff.mdmColumnsForBuyingPlan.enabled,
      ),
    onSuccess: (newColumn) => {
      queryClient.setQueryData<BuilderSchemaResponse>(
        [QueryKey.BUILDER_SCHEMA, params.campaignId],
        (oldData) => {
          return oldData
            ? {
                ...oldData,
                customColumns: [...oldData.customColumns, newColumn],
              }
            : oldData;
        },
      );
    },
  });

  return mutation.mutateAsync;
};

export const useEditCustomColumn = () => {
  const params = routeApi.useParams();
  const queryClient = useQueryClient();
  const ff = useFlags('@mint/arm', ['mdmColumnsForBuyingPlan']);

  const mutation = useMutation({
    mutationFn: (payload: EditCustomColumnPayload) =>
      editCustomColumn(payload, ff.mdmColumnsForBuyingPlan.enabled),
    onSuccess: (newColumn) => {
      queryClient.setQueryData<BuilderSchemaResponse>(
        [QueryKey.BUILDER_SCHEMA, params.campaignId],
        (oldData) => {
          return oldData
            ? {
                ...oldData,
                customColumns: oldData.customColumns.map((column) => {
                  if (column.id === newColumn.id) {
                    return newColumn;
                  }
                  return column;
                }),
              }
            : oldData;
        },
      );
    },
  });

  return mutation.mutateAsync;
};

export const useDeleteCustomColumn = () => {
  const params = routeApi.useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (columnId: string) => deleteCustomColumn(columnId),
    onSuccess: (deletedColumn) => {
      queryClient.setQueryData<BuilderSchemaResponse>(
        [QueryKey.BUILDER_SCHEMA, params.campaignId],
        (oldData) => {
          return oldData
            ? {
                ...oldData,
                customColumns: oldData.customColumns.filter(
                  (column) => column.id !== deletedColumn.id,
                ),
              }
            : oldData;
        },
      );
    },
  });

  return mutation.mutateAsync;
};
